import { type QueryClient, queryOptions } from "@tanstack/react-query"

import { axiosInstance } from "@/lib/api-client"
import { API_ROUTES } from "@/lib/api-routes"
import { useAuthStore } from "@/lib/stores/auth-store"
import type { Customer } from "@/lib/types"

/**
 * The current session, resolved by calling `GET /customer/auth/session`
 * (200 → the `Customer`, 401 → not authenticated). This is THE source of
 * truth for "am I logged in?" — there is no token to inspect, because
 * auth is an HttpOnly cookie the browser sends automatically.
 *
 * Factored into `queryOptions` so the same definition backs both
 * `useSessionQuery` (components) and route `beforeLoad` guards
 * (`ensureQueryData`). Side effects (hydrating / clearing the Zustand
 * auth store) live in `queryFn` so they fire for either caller.
 */
export const SESSION_QUERY_KEY = ["auth", "session"] as const

export function sessionQueryOptions() {
  return queryOptions({
    queryKey: SESSION_QUERY_KEY,
    queryFn: async () => {
      try {
        const res = await axiosInstance.get<Customer>(API_ROUTES.AUTH.SESSION)
        useAuthStore.getState().setUser(res.data)
        return res.data
      } catch (error) {
        useAuthStore.getState().clearSession()
        throw error
      }
    },
    retry: false,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  })
}

/**
 * `beforeLoad` helper: returns the resolved user, or `null` if the
 * session probe fails (401 / network). The route decides how to react.
 */
export async function resolveSession(
  queryClient: QueryClient
): Promise<Customer | null> {
  try {
    return await queryClient.ensureQueryData(sessionQueryOptions())
  } catch {
    return null
  }
}
